import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DropdownComponent } from '@app/shared/dropdown/dropdown.component';
import { ButtonPickerComponent } from '@app/shared/button-picker/button-picker.component';
import { ButtonComponent } from '@app/shared/button/button.component';
import { SelectComponent } from '@shared/modules/select/select.component';
import { ResidentialComplexSelectorComponent } from '@app/real-property-list-filter/components/residential-complex-selector/residential-complex-selector.component';
import { DistrictSelectorComponent } from '@app/real-property-list-filter/components/district-selector/district-selector.component';
import { StreetSelectorComponent } from '@app/real-property-list-filter/components/street-selector/street-selector.component';
import { SettingsService } from '@app/settings.service';

import * as models from '@app/real-property-list-filter/models';

import * as sharedServices from '@shared/services';
import * as services from '../../../../real-property-list-filter/services';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { RealPropertyService } from '@modules/real-property-details';

@Component({
  selector: 'app-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    ButtonPickerComponent,
    ButtonComponent,
    SelectComponent,
    ResidentialComplexSelectorComponent,
    DistrictSelectorComponent,
    StreetSelectorComponent,
  ],
})
export class FilterModalComponent implements OnInit, OnDestroy {

  public dealTypes = models.operationOptions;
  public layoutOptions = models.objectLayoutOptions;

  public houseConditions$: Observable<any>;
  public houseConditionsLoading$: Observable<boolean>;

  public cityList$: Observable<any>;
  public cityListLoading$: Observable<boolean>;

  public currentRoominess$: Observable<any>;

  public currentRealPropertyCount$: Observable<number>;

  years: { label: string; value: string }[] = [];

  form: FormGroup;
  filters: any;

  isTablet$: Observable<boolean>;
  isMobile$: Observable<boolean>;

  destroy$: Subject<any> = new Subject<any>();

  constructor(
    private _dialogConfig: DynamicDialogConfig,
    private _dialogRef: DynamicDialogRef,
    private _dictService: sharedServices.DictionaryService,
    private _addressService: sharedServices.AddressService,
    private _settingsService: SettingsService,
    private _complexService: services.ResidentialComplexService,
    public realPropertyListFilterService: services.RealPropertyListFilterService,
    private _realPropertyService: RealPropertyService,
  ) {}

  ngOnInit(): void {
    this.filters = this._dialogConfig.data.filters;
    this.isTablet$ = this._settingsService.isTablet();
    this.isMobile$ = this._settingsService.isMobile();
    this.currentRealPropertyCount$ = this._realPropertyService.realPropertyTotalCountFilter$;
    this.currentRoominess$ = this._settingsService.currentFilters$.pipe(map((f) => f.roominess));
    this.cityList$ = this._addressService.cityList$.pipe(
      map((cities) => {
        return cities.map((city) => ({ value: city.id, label: city.name }));
      }),
    );
    this.cityListLoading$ = this._addressService.cityLoading$;

    this.houseConditions$ = this._dictService.houseConditions$.pipe(
      map((conditions) => {
        return conditions.map((cond: any) => ({ value: cond.id, label: cond.value }));
      }),
    );
    this.houseConditionsLoading$ = this._dictService.houseConditionLoading$;
    this._addressService.currentCity$.pipe(filter((c) => !!c), takeUntil(this.destroy$))
      .subscribe((city) => {
        this.filters['city'] = city.id;
        this.buildForm(this.filters);
      });

    combineLatest([
      this._addressService.currentStreets$.pipe(map((st) => st.map((s) => s.id))),
      this._addressService.currentDistricts$.pipe(map((dt) => dt.map((s: any) => s.id))),
      this._dictService.currentHouseConditions$.pipe(map((st: any[]) => st.map((s) => s.id))),
      this._complexService.currentComplex$.pipe(map((st) => st.map((s: any) => s.id))),
      this._settingsService.currentFilters$.pipe(map((f) => ({ layout: f.layout, proposalId: f.proposalId, constructYearFrom: f.constructYear.from, constructYearTo: f.constructYear.to }))),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([street, district, condition, complex, filters]) => {
        this.form.patchValue({
          street,
          district,
          condition: condition[0],
          complex,
          constructYearFrom: filters.constructYearFrom,
          constructYearTo: filters.constructYearTo,
          proposalId: filters.proposalId,
          layout: filters.layout,
        });
      });
  }

  buildForm(filters: any): void {
    this.form = new FormGroup<any>({
      dealType: new FormControl(filters.dealType),
      realtyType: new FormControl(filters.realtyType),
      priceFrom: new FormControl(filters.priceFrom),
      priceTo: new FormControl(filters.priceTo),
      areaFrom: new FormControl(filters.areaFrom),
      areaTo: new FormControl(filters.areaTo),
      proposalId: new FormControl(),
      constructYearFrom: new FormControl(),
      constructYearTo: new FormControl(),
      city: new FormControl(filters.city),
      complex: new FormControl([]),
      district: new FormControl([]),
      street: new FormControl([]),
      layout: new FormControl(),
      condition: new FormControl(),
      roominess: new FormControl(filters.roominess),
    });
  }

  onCitySelect(cityCode: number) {
    this._addressService.updateCurrentCity(cityCode);
    this._addressService.updateCurrentDistricts([]);
    this.form.patchValue({ complex: null, street: null, district: null });
    this._settingsService.updateFiltersCount({ city: cityCode, complex: [], street: [], district: [] });
  }

  conditionSelect(id: number) {
    const condition = id ? [id] : [];
    this._dictService.updateCurrentHouseCondition(condition);
    this._realPropertyService.getRealPropertyCountDispatch({ housingConditionIds: condition });
    this._settingsService.updateFiltersCount({ condition: id });
  }

  updateFilter(action: string, value: any) {
    const filters = {};
    switch (action) {
      case 'roominess':
        filters['roominess'] = value;
        this._realPropertyService.getRealPropertyCountDispatch({ numberOfRooms: value });
        this.form.get('roominess').setValue(value);
        this._settingsService.updateFiltersCount({ roominess: value });
        break;
      case 'constructYearFrom':
      case 'constructYearTo':
        filters['constructYear'] = { from: this.form.get('constructYearFrom').value, to: this.form.get('constructYearTo').value };
        this._realPropertyService.getRealPropertyCountDispatch({ yearOfConstruction: { from: this.form.get('constructYearFrom').value, to: this.form.get('constructYearTo').value } });
        this._settingsService.updateFiltersCount({ constructYearFrom: this.form.get('constructYearFrom').value, constructYearTo: this.form.get('constructYearTo').value });
        break;
      case 'layout':
        filters['layout'] = value;
        this._realPropertyService.getRealPropertyCountDispatch({ atelier: value });
        this._settingsService.updateFiltersCount({ layout: value });
        break;
      case 'areaFrom':
      case 'areaTo':
        filters['area'] = { from: this.form.get('areaFrom').value, to: this.form.get('areaTo').value };
        this._realPropertyService.getRealPropertyCountDispatch({ totalArea: { from: this.form.get('areaFrom').value, to: this.form.get('areaTo').value } });
        this._settingsService.updateFiltersCount({ areaFrom: this.form.get('areaFrom').value, areaTo: this.form.get('areaTo').value });
        break;
      case 'priceFrom':
      case 'priceTo':
        filters['price'] = { from: this.form.get('priceFrom').value, to: this.form.get('priceTo').value };
        this._realPropertyService.getRealPropertyCountDispatch({ objectPrice: { from: this.form.get('priceFrom').value, to: this.form.get('priceTo').value } });
        this._settingsService.updateFiltersCount({ priceFrom: this.form.get('priceFrom').value, priceTo: this.form.get('priceTo').value });
        break;
      case 'proposalId':
        filters['proposalId'] = this.form.get('proposalId').value;
        this._realPropertyService.getRealPropertyCountDispatch({ applicationId: this.form.get('proposalId').value });
        this._settingsService.updateFiltersCount({ proposalId: this.form.get('proposalId').value });
        break;
      case 'dealType':
        filters['dealType'] = this.dealTypes.find((item) => item.value === value);
        this._realPropertyService.getRealPropertyCountDispatch({ operationTypeId: value });
        this._settingsService.updateFiltersCount({ dealType: value });
        break;
    }
    this._settingsService.updateFilters(filters);
  }

  closeDialog() {
    this._dialogRef.close({ action: 'close', formData: this.form.value });
  }

  clearFilters() {
    this.form.patchValue({
      dealType: 2,
      realtyType: null,
      priceFrom: null,
      priceTo: null,
      areaFrom: null,
      areaTo: null,
      proposalId: null,
      constructYearFrom: null,
      constructYearTo: null,
      city: 1,
      complex: [],
      district: [],
      street: [],
      layout: null,
      condition: null,
      roominess: [],
    });
    this._settingsService.clearFilter();
    this._settingsService.updateFilters({ ...this.form.value, city: 1, dealType: 2 });
  }

  onSearch() {
    this._dialogRef.close({ action: 'search', formData: this.form.value });
  }

  saveSearch() {
    this._dialogRef.close({ action: 'save', formData: this.form.value });
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}